import axios, { AxiosError } from 'axios'
import tools from '@/composables/tools'

export const WEBMAIL_ADMIN_PERMISO = 'ver webmail administracion@tecnonet.es'

export interface CorreoEstado {
  ok: boolean
  configurado: boolean
  cuenta: string
  imap_disponible?: boolean
  mensaje?: string | null
}

export interface CorreoCarpeta {
  nombre: string
  ruta: string
  mensajes?: number | null
  no_leidos?: number | null
}

export interface CorreoResumen {
  uid: number
  asunto: string
  de: string
  de_email?: string | null
  para?: string | string[]
  fecha: string
  visto: boolean
  tiene_adjuntos?: boolean
}

export interface CorreoAdjuntoMeta {
  nombre: string
  mime?: string
  size?: number
  part?: string
}

export interface CorreoDetalle extends CorreoResumen {
  carpeta?: string
  cc?: string[]
  cuerpo?: string
  cuerpo_html?: string | null
  adjuntos?: CorreoAdjuntoMeta[]
}

export interface CorreoListaFiltros {
  carpeta?: string
  desde?: string
  hasta?: string
  de?: string
  para?: string
  para_filtro?: string
  asunto?: string
  texto?: string
  solo_no_leidos?: boolean | number
  limit?: number
  offset?: number
}

export interface CorreoListaResponse {
  ok: boolean
  carpeta: string
  total: number
  limit: number
  offset: number
  mensajes: CorreoResumen[]
  error?: string
}

export interface CorreoAdjuntoPayload {
  nombre: string
  contenido_base64: string
  mime?: string
}

export interface CorreoEnviarPayload {
  para: string[]
  cc?: string[]
  cco?: string[]
  asunto: string
  cuerpo: string
  cuerpo_html?: string
  reply_to?: string
  adjuntos?: CorreoAdjuntoPayload[]
  entidad_id?: number
}

function apiError(error: unknown, fallback: string): string {
  const ax = error as AxiosError<{ error?: string; message?: string }>
  return ax.response?.data?.error
    || ax.response?.data?.message
    || fallback
}

export abstract class CorreoAdministracionService {
  static async estado(): Promise<CorreoEstado | null> {
    try {
      const { data } = await axios.get<CorreoEstado>('correo-administracion/estado')
      return data
    } catch (error) {
      console.error('CorreoAdministracionService.estado', error)
      tools().toast(apiError(error, 'No se pudo consultar el estado del buzón'), 'danger', 3500)
      return null
    }
  }

  static async carpetas(): Promise<CorreoCarpeta[]> {
    try {
      const { data } = await axios.get<{ ok: boolean; carpetas: CorreoCarpeta[] }>('correo-administracion/carpetas')
      return data.carpetas || []
    } catch (error) {
      console.error('CorreoAdministracionService.carpetas', error)
      tools().toast(apiError(error, 'No se pudieron cargar las carpetas'), 'danger', 3500)
      return []
    }
  }

  static async listar(filtros: CorreoListaFiltros): Promise<CorreoListaResponse | null> {
    try {
      const { data } = await axios.get<CorreoListaResponse>('correo-administracion/mensajes', { params: filtros })
      return data
    } catch (error) {
      console.error('CorreoAdministracionService.listar', error)
      tools().toast(apiError(error, 'No se pudieron cargar los mensajes'), 'danger', 3500)
      return null
    }
  }

  static async leer(uid: number, carpeta: string, marcarLeido = false): Promise<CorreoDetalle | null> {
    try {
      const { data } = await axios.get<{ ok: boolean; mensaje: CorreoDetalle }>(
        `correo-administracion/mensajes/${uid}`,
        { params: { carpeta, marcar_leido: marcarLeido ? 1 : 0 } }
      )
      return data.mensaje || null
    } catch (error) {
      console.error('CorreoAdministracionService.leer', error)
      tools().toast(apiError(error, 'No se pudo abrir el mensaje'), 'danger', 3500)
      return null
    }
  }

  static filenameFromDisposition(header?: string, fallback = 'adjunto'): string {
    if (!header) return fallback
    const utf = header.match(/filename\*=UTF-8''([^;]+)/i)
    if (utf?.[1]) {
      try {
        return decodeURIComponent(utf[1].trim())
      } catch {
        return utf[1].trim()
      }
    }
    const plain = header.match(/filename="?([^";]+)"?/i)
    return plain?.[1]?.trim() || fallback
  }

  static async descargarAdjunto(
    uid: number,
    part: string,
    carpeta: string,
    nombre?: string,
  ): Promise<boolean> {
    if (!part) {
      tools().toast('Este adjunto no tiene parte MIME para descargar', 'warning', 3000)
      return false
    }
    try {
      const response = await axios.get(
        `correo-administracion/mensajes/${uid}/adjuntos/${encodeURIComponent(part)}`,
        {
          params: { carpeta: carpeta || 'INBOX' },
          responseType: 'blob',
        },
      )
      const blob = response.data as Blob
      const filename = this.filenameFromDisposition(
        String(response.headers['content-disposition'] || ''),
        nombre || 'adjunto',
      )
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      return true
    } catch (error) {
      console.error('CorreoAdministracionService.descargarAdjunto', error)
      const ax = error as AxiosError<Blob>
      let message = 'No se pudo descargar el adjunto'
      if (ax.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await ax.response.data.text()) as { error?: string; message?: string }
          message = parsed.error || parsed.message || message
        } catch {
          // el cuerpo no es JSON
        }
      }
      tools().toast(message, 'danger', 4000)
      return false
    }
  }

  static async enviar(payload: CorreoEnviarPayload): Promise<boolean> {
    try {
      const { data } = await axios.post<{ ok: boolean; error?: string }>(
        'correo-administracion/enviar',
        payload
      )
      if (!data.ok) {
        tools().toast(data.error || 'No se pudo enviar el correo', 'danger', 4000)
        return false
      }
      tools().toast('Correo enviado', 'success', 2500)
      return true
    } catch (error) {
      console.error('CorreoAdministracionService.enviar', error)
      tools().toast(apiError(error, 'No se pudo enviar el correo'), 'danger', 4000)
      return false
    }
  }
}
